import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  Button,
  HStack,
  Text,
  VStack,
  Card,
  CardBody,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Skeleton,
  Tooltip,
  Tabs,
  TabList,
  Tab,
} from '@chakra-ui/react';
import { ListBullets, MagnifyingGlass, Export } from 'phosphor-react';
import { useQuery } from '@tanstack/react-query';
import { useMonitoramento, ILogBot, TipoBot } from '../../../hooks/useMonitoramento';
import { PaginatorLocal } from './PaginatorLocal';
import { BotDetailDrawer } from './BotDetailDrawer';
import { formatDate, getFlagColor } from '../utils/formatters';

const TIPO_BOTS: TipoBot[] = ['RPA', 'ETL', 'WEBSCRAP'];

export const LogsBrowserView: React.FC = () => {
  const [tipoIndex, setTipoIndex] = useState(0);
  const [flagFilter, setFlagFilter] = useState('');
  const [selectedBot, setSelectedBot] = useState<{
    name: string;
    tipo: TipoBot;
    defaultTab?: number;
    execFilter?: string;
  } | null>(null);
  const [botNameFilter, setBotNameFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const tipoBot = TIPO_BOTS[tipoIndex];

  const {
    getLogsRPA,
    getLogsETL,
    getLogsWebScrap,
    getBotsRPA,
    getBotsETL,
    getBotsWebScrap,
    exportarLogsCSV,
  } = useMonitoramento();

  // Get fetcher for current tipo
  const fetcherMap = { RPA: getLogsRPA, ETL: getLogsETL, WEBSCRAP: getLogsWebScrap };
  const botsFetcherMap = { RPA: getBotsRPA, ETL: getBotsETL, WEBSCRAP: getBotsWebScrap };
  const fetcher = fetcherMap[tipoBot];
  const botsFetcher = botsFetcherMap[tipoBot];

  // Debounce search
  const searchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchDebounced(value);
    }, 400);
  };

  // Filters
  const filters = useMemo(
    () => ({
      page,
      size: pageSize,
      bot_name: botNameFilter || undefined,
      flag: flagFilter || undefined,
      content_line: searchDebounced || undefined,
    }),
    [page, botNameFilter, flagFilter, searchDebounced],
  );

  const { data: logsResp, isLoading } = useQuery({
    queryKey: ['logsBrowser', filters, tipoBot],
    queryFn: fetcher,
    // 60s (era 30s): a listagem de logs bate count()+find na time-series; reduz a
    // pressao no Mongo. O backend ainda cacheia 20s, entao o dado nao fica >20s stale.
    refetchInterval: 60000,
  });

  const { data: botsList } = useQuery({
    queryKey: ['logsBrowserBots', tipoBot],
    queryFn: () => botsFetcher(),
  });

  const logs: ILogBot[] = logsResp?.data ?? [];
  const pagination = logsResp?.pagination;

  const handleExport = () => {
    exportarLogsCSV(tipoBot, {
      bot_name: botNameFilter || undefined,
      flag: (flagFilter as 'INFO' | 'WARNING' | 'ERROR') || undefined,
    });
  };

  const resetFilters = () => {
    setBotNameFilter('');
    setFlagFilter('');
    setSearch('');
    setSearchDebounced('');
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setPage(1);
  };

  return (
    <Box p={6}>
      {/* Header */}
      <HStack mb={6} spacing={3} justify="space-between" flexWrap="wrap">
        <HStack spacing={3}>
          <Icon as={ListBullets} boxSize={7} color="purple.500" />
          <Heading size="lg">Logs</Heading>
        </HStack>
        <Button
          size="sm"
          leftIcon={<Export />}
          colorScheme="blue"
          variant="outline"
          onClick={handleExport}
        >
          Exportar CSV
        </Button>
      </HStack>

      {/* Tipo Bot Tabs */}
      <Tabs
        index={tipoIndex}
        onChange={i => {
          setTipoIndex(i);
          setPage(1);
        }}
        size="sm"
        variant="soft-rounded"
        colorScheme="blue"
        mb={4}
      >
        <TabList>
          {TIPO_BOTS.map(t => (
            <Tab key={t}>{t}</Tab>
          ))}
        </TabList>
      </Tabs>

      {/* Filters */}
      <Flex gap={3} mb={4} flexWrap="wrap" align="center">
        <Select
          size="sm"
          maxW="200px"
          placeholder="Todos os bots"
          value={botNameFilter}
          onChange={e => {
            setBotNameFilter(e.target.value);
            setPage(1);
          }}
        >
          {(botsList || []).map((b: string) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </Select>

        <Select
          size="sm"
          maxW="140px"
          placeholder="Todas as flags"
          value={flagFilter}
          onChange={e => {
            setFlagFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="INFO">INFO</option>
          <option value="WARNING">WARNING</option>
          <option value="ERROR">ERROR</option>
        </Select>

        <InputGroup maxW="250px" size="sm">
          <InputLeftElement>
            <Icon as={MagnifyingGlass} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Buscar no conteudo..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </InputGroup>

        {(botNameFilter || flagFilter || search) && (
          <Button size="sm" variant="ghost" colorScheme="red" onClick={resetFilters}>
            Limpar
          </Button>
        )}
      </Flex>

      {/* Table */}
      <Card>
        <CardBody p={0}>
          <Box overflowX="auto">
            {isLoading ? (
              <VStack spacing={2} p={4}>
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} h="36px" w="100%" />
                ))}
              </VStack>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Timestamp</Th>
                    <Th>Flag</Th>
                    <Th>Bot</Th>
                    <Th>Empresa</Th>
                    <Th>Mensagem</Th>
                    <Th>Exec ID</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {logs.map(log => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Tr
                      key={`${log.exec_id}-${log.timestamp}-${log.content_line?.slice(0, 20)}`}
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Td fontSize="xs" whiteSpace="nowrap">
                        {formatDate(log.timestamp)}
                      </Td>
                      <Td>
                        <Badge colorScheme={getFlagColor(log.flag)} fontSize="xs">
                          {log.flag}
                        </Badge>
                      </Td>
                      <Td>
                        <Text
                          fontSize="xs"
                          cursor="pointer"
                          color="blue.500"
                          _hover={{ textDecoration: 'underline' }}
                          onClick={() => setSelectedBot({ name: log.bot_name, tipo: tipoBot })}
                        >
                          {log.bot_name}
                        </Text>
                      </Td>
                      <Td fontSize="xs">{log.codi_emp || '-'}</Td>
                      <Td maxW="400px">
                        <Tooltip label={log.content_line} placement="top" hasArrow>
                          <Text fontSize="xs" noOfLines={2}>
                            {log.content_line}
                          </Text>
                        </Tooltip>
                      </Td>
                      <Td>
                        {log.exec_id && (
                          <Text
                            fontSize="xs"
                            cursor="pointer"
                            color="blue.500"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() =>
                              setSelectedBot({
                                name: log.bot_name,
                                tipo: tipoBot,
                                defaultTab: 1,
                                execFilter: log.exec_id,
                              })
                            }
                            fontFamily="mono"
                          >
                            {log.exec_id.slice(0, 8)}
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                  {logs.length === 0 && (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={12}>
                        <VStack spacing={2}>
                          <Icon as={ListBullets} boxSize={8} color="gray.300" />
                          <Text color="gray.400" fontSize="sm">
                            {botNameFilter || flagFilter || search
                              ? 'Nenhum log encontrado com esses filtros'
                              : 'Nenhum log encontrado'}
                          </Text>
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            )}
          </Box>

          {/* Pagination */}
          {pagination && (
            <Box px={4} pb={2}>
              <PaginatorLocal
                page={pagination.page}
                pages={pagination.total_pages}
                total={pagination.total}
                hasNext={pagination.has_next}
                hasPrevious={pagination.has_previous}
                onPageChange={setPage}
                label="logs"
              />
            </Box>
          )}
        </CardBody>
      </Card>
      {selectedBot && (
        <BotDetailDrawer
          isOpen={!!selectedBot}
          onClose={() => setSelectedBot(null)}
          botName={selectedBot.name}
          tipoBot={selectedBot.tipo}
          defaultTab={selectedBot.defaultTab}
          defaultExecFilter={selectedBot.execFilter}
        />
      )}
    </Box>
  );
};
